import { API_URLs } from "../../constants/api_urls";
import { apiConfigs } from "../../constants/apiConfigs";
import { Coordinates } from "../../types/address";
import { IGAccountsResponse, Influencer, MediaProductType, MediaType, PostsData, SortOption } from "../../types/influencer/influencerTypes";
import { Insights } from "../../types/influencer/insightsTypes";
import axiosClient from "../../utils/axiosClient";
import { getErrorMessage } from "../../utils/helpers";

export const fetchInfluencer = async (token?: string) => {
    let config: any = {};
    if (token) config.headers = { Authorization: `Bearer ${token}` };
    const response = await axiosClient.get(API_URLs.INFLUENCER_PROFILE, config);
    return response.data;
}

export const updateInfluencer = async (influencer: Influencer): Promise<Influencer> => {
    const response = await axiosClient.put(API_URLs.INFLUENCER_PROFILE, influencer);
    return response.data.influencer as Influencer;
}

export const getIGAuthURL = async (feature: string|null = null, redirect_url: string, force?: string | null, social_account_id?: string | null) : Promise<any> => {
    const response = await axiosClient.get(apiConfigs.GET_IG_AUTH_URL.url, {params: {redirect_url, ...(feature ? {feature} : {}), ...(force ? {force} : {}), ...(social_account_id ? {social_account_id} : {})}})
    return response.data.url;
}

export const storeAccessToken = async (code: string, redirect_url: string, via: string = 'ig'): Promise<any> => {
    const response = await axiosClient.post(API_URLs.STORE_ACCESS_TOKEN + "?via=" + via, { code, redirect_url });
    return response.data as any;
}

export const fetchInstagramAccounts = async (): Promise<IGAccountsResponse> => {
    const response = await axiosClient.get(API_URLs.LIST_IG_ACCOUNTS);
    return response.data as IGAccountsResponse;
}

export const linkInstagramAccount = async (accountId: string, via: string = 'ig'): Promise<boolean> => {
    const response = await axiosClient.get(API_URLs.LINK_IG_ACCOUNT.replace('<accountId>', accountId) + "?via=" + via);
    if (response.data.status) {
        return true;
    }
    switch (response.data.error) {
        case 'linked_elsewhere':
            throw new Error(`This Instagram is already linked to another user with mobile number ending with ${response.data.mobile}`)
        case 'account_mismatch':
            throw new Error('Kindly connect the same Professional Instagram account that you already have linked earlier.');
        case '404':
            throw new Error("No page found");
        default:
            throw new Error(getErrorMessage(response.data, 'Failed to link Instagram account'))
    }
}

export const fetchInsights = async (): Promise<Insights> => {
    const response = await axiosClient.get(API_URLs.INSIGHTS);
    return response.data.insights as Insights;
}

export const fetchPosts = async (media_product_type: MediaType, page: number): Promise<PostsData> => {
    const response = await axiosClient.get(API_URLs.INFLUENCER_MEDIA, { params: { media_product_type, page, sort: SortOption.TIMESTAMP } })
    return response.data;
}

export const fetchSocialPosts = async (socialAccountId: string, type: MediaProductType, page: number)  => {
    const response = await axiosClient.get(apiConfigs.FETCH_SOCIAL_POSTS.url.replace('<socialAccountId>', socialAccountId), { params: { ...(type !== MediaProductType.VIDEO ? { type } : {}), page, sort: SortOption.TIMESTAMP_TZ } });
    return response.data;
}

export const fetchPost = async (postId: string) => {
    const response = await axiosClient.get(API_URLs.INFLUENCER_POST.replace('<postId>', postId));
    return response.data.post;
}

export const fetchSocialPost = async (socialAccountId: string, socialPostId: string) => {
    const response = await axiosClient.get(apiConfigs.FETCH_SOCIAL_POST.url.replace('<socialAccountId>', socialAccountId).replace('<socialPostId>', socialPostId));
    return response.data.social_post;
}


export const fetchLatestInfluencerMedia = async () => {
    const response = await axiosClient.post(API_URLs.FETCH_MEDIA);
    return response.data;
}
export const fetchLatestInfluencerSocialPosts = async (socialAccountId: string) => {
    const response = await axiosClient.post(API_URLs.FETCH_MEDIA.replace('<socialAccountId>', socialAccountId));
    return response.data;
}

export const fetchCities = async (q: string = ''): Promise<string[]> => {
    const response = await axiosClient.get(API_URLs.FETCH_CITIES, { params: { q } })
    return response.data.results as string[];
}

export const fetchBasicPost = async (postId: string) => {
    const response = await axiosClient.get(apiConfigs.FETCH_POST_BASIC.url.replace('<postId>', postId));
    return response.data.post;
}

export const fetchBasicSocialPost = async (postId: string) => {
    const response = await axiosClient.get(apiConfigs.FETCH_SOCIAL_POST_BASIC.url.replace('<postId>', postId));
    return response.data.social_post;
}

export const fetchNearestCity = async (coordinates: Coordinates) => {
    const response = await axiosClient.post(API_URLs.FETCH_NEAREST_CITY, { latlng: `${coordinates.latitude},${coordinates.longitude}` })
    return response.data;
}

export const updateInfluencerLocation = async (payload: any) => {
    const response = await axiosClient.post(apiConfigs.UPDATE_INFLUENCER_LOCATION.url, payload);
    return response.data;
}

export const addSocialAccount = async (payload: {platform: string, handle: string}) => {
    const response = await axiosClient.post(apiConfigs.ADD_SOCIAL_ACCOUNT.url, payload);
    return response.data
}

export const updateChargePerReel = async (socialAccountId: string, chargePerReel: number) => {
    const response = await axiosClient.post(
        apiConfigs.UPDATE_CHARGE_PER_REEL.url.replace('<socialAccountId>', socialAccountId),
        { charge_per_reel: chargePerReel }
    );
    return response.data;
}

export const fetchAccountDeletionRequests = async () => {
    const response = await axiosClient.get(apiConfigs.FETCH_ACCOUNT_DELETION_REQUESTS.url);
    return response.data.items ?? [];
}

export const requestAccountDeletion = async () => {
    const response = await axiosClient.post(apiConfigs.REQUEST_ACCOUNT_DELETION.url);
    return response.data;
}

export const fetchTickerFeed = async () => {
    const response = await axiosClient.get(apiConfigs.FETCH_TICKER_FEED.url);
    return response.data.items ?? [];
}